/**
 * Reddit URL Parser Utility
 *
 * Parses Reddit URLs to extract post IDs for searching.
 * Supports multiple Reddit URL formats including www, old, and plain domains.
 *
 * SESSION_S_035_003: Reddit URL search functionality
 */

/**
 * Result of parsing a Reddit URL
 */
export interface RedditUrlParseResult {
  /** Whether the URL is a valid Reddit post URL */
  isValid: boolean;
  /** The extracted Reddit post ID (e.g., "abc123"), null if invalid */
  postId: string | null;
  /** Error message if URL is invalid, null if valid */
  errorMessage: string | null;
}

/**
 * Regex pattern to match Reddit post URLs and extract the post ID.
 *
 * Supported formats:
 * - https://www.reddit.com/r/Seattle/comments/abc123/post_title_here/
 * - https://old.reddit.com/r/Seattle/comments/abc123/post_title_here/
 * - https://reddit.com/r/Seattle/comments/abc123/
 * - https://www.reddit.com/comments/abc123
 *
 * The pattern captures the post ID from the /comments/{postId} portion.
 */
const REDDIT_URL_PATTERN = /reddit\.com(?:\/r\/[^/]+)?\/comments\/([a-z0-9]+)/i;

/**
 * Parse a Reddit URL to extract the post ID
 *
 * @param url - The Reddit URL to parse
 * @returns Parse result with validity status, post ID, and error message
 *
 * @example
 * // Valid URL
 * const result = parseRedditUrl('https://www.reddit.com/r/Seattle/comments/abc123/post_title_here/');
 * // { isValid: true, postId: 'abc123', errorMessage: null }
 *
 * @example
 * // Invalid URL
 * const result = parseRedditUrl('https://google.com');
 * // { isValid: false, postId: null, errorMessage: 'Not a valid Reddit URL' }
 */
export function parseRedditUrl(url: string): RedditUrlParseResult {
  // Trim whitespace
  const trimmedUrl = url.trim();

  // Check for empty input
  if (!trimmedUrl) {
    return {
      isValid: false,
      postId: null,
      errorMessage: 'Please enter a Reddit URL'
    };
  }

  // Check if it looks like a URL at all
  if (!trimmedUrl.includes('reddit.com')) {
    return {
      isValid: false,
      postId: null,
      errorMessage: 'Not a Reddit URL. Please enter a reddit.com link.'
    };
  }

  // Try to match the pattern
  const match = REDDIT_URL_PATTERN.exec(trimmedUrl);

  if (!match?.[1]) {
    return {
      isValid: false,
      postId: null,
      errorMessage: 'Invalid Reddit post URL. Please enter a link to a Reddit post (contains /comments/).'
    };
  }

  // Validate that the extracted post ID is reasonable
  const postId = match[1];

  // Reddit post IDs are typically 6-7 alphanumeric characters
  if (postId.length < 5 || postId.length > 10) {
    return {
      isValid: false,
      postId: null,
      errorMessage: 'Invalid post ID in URL. Please check the URL and try again.'
    };
  }

  return {
    isValid: true,
    postId,
    errorMessage: null
  };
}

/**
 * Convert a short Reddit post ID to the full Reddit post ID format
 *
 * The backend stores post IDs in the format "t3_abc123",
 * but Reddit URLs only contain "abc123".
 *
 * @param shortPostId - The short post ID from the URL (e.g., "abc123")
 * @returns The full Reddit post ID (e.g., "t3_abc123")
 */
export function toFullRedditPostId(shortPostId: string): string {
  // If already prefixed, return as-is
  if (shortPostId.startsWith('t3_')) {
    return shortPostId;
  }
  return `t3_${shortPostId}`;
}
